import React, {useEffect, useState} from "react";
import {Alert, Button, Col, Input, Radio, Row, Select, Switch, Tooltip} from "antd";
import {
    ApartmentOutlined,
    CloseOutlined,
    EditOutlined,
    EyeOutlined,
    FlagOutlined,
    InfoCircleOutlined,
    TeamOutlined,
    UserOutlined
} from "@ant-design/icons";
import {useTranslation} from "react-i18next";
import {useStore} from "hooks/useStore";
import {
    TICKET_ADMIN_ROLES,
    IS_TICKET_EXTERNAL_ACCESS_BLOCKED,
    TICKET_PRIORITIES
} from "constants/ticket";
import {
    ActionRow,
    AssignCard,
    AssignedBand,
    EditToggle,
    FieldLabel,
    InfoColumn,
    ModeSwitch,
    OrBadge,
    SectionCard,
    SectionTitle,
    SelfAssignRow,
    TargetField,
    TargetRow
} from "./TicketDetail.styles";
import {InfoItem} from "./TicketInfoBand";

const ASSIGN_MODES = {self: "self", other: "other"};
const USER_SEARCH_DELAY = 300;

export const TicketManagementArea = ({ticket, can, roleOptions, assignTargets, onChanged}) => {
    const {t} = useTranslation(["tickets"]);
    const stores = useStore();
    const canClaim = can("canClaim");
    const [mode, setMode] = useState(canClaim ? ASSIGN_MODES.self : ASSIGN_MODES.other);
    const departmentOptions = assignTargets
        ? roleOptions.filter(role => assignTargets.roles.includes(role.key))
        : roleOptions;
    // Seçilebilir olmayan roller seçili gelirse çipte ham rol kodu görünür
    const assignedRoles =
        ticket.assignedTo?.type === "role"
            ? []
                  .concat(ticket.assignedTo.role || [])
                  .filter(role => departmentOptions.some(option => option.key === role))
            : [];
    const assignedRolesKey = [...assignedRoles].sort().join(",");
    const [roles, setRoles] = useState(assignedRoles);
    const [selectedUser, setSelectedUser] = useState();
    const [userSearch, setUserSearch] = useState("");
    const [users, setUsers] = useState([]);
    const [isEditing, setIsEditing] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [visibilitySubmitting, setVisibilitySubmitting] = useState(false);
    const isOtherMode = !canClaim || mode === ASSIGN_MODES.other;

    useEffect(() => {
        setRoles(assignedRoles);
        setSelectedUser(undefined);
    }, [assignedRolesKey, ticket.assignedTo?.userId]);

    useEffect(() => {
        if (!isEditing || !isOtherMode) {
            return undefined;
        }
        let isCurrent = true;
        const timer = setTimeout(
            () => {
                stores.ticketStore
                    .getAssignableUsers(undefined, ticket._id, userSearch)
                    .then(response => {
                        if (isCurrent) {
                            setUsers(response?.data?.users || []);
                        }
                    });
            },
            userSearch ? USER_SEARCH_DELAY : 0
        );
        return () => {
            isCurrent = false;
            clearTimeout(timer);
        };
    }, [isEditing, isOtherMode, userSearch]);

    const roleNameByKey = Object.fromEntries(roleOptions.map(role => [role.key, role.name]));
    // Admin rolleri departman listesinde olmadığından etiketi kullanıcı tipi çevirisinden alınır
    const getRoleLabel = role =>
        roleNameByKey[role] ||
        (TICKET_ADMIN_ROLES.includes(role) ? t(role, {ns: "userTypes"}) : null);
    const getUserLabel = user => {
        const roleLabel = getRoleLabel(user.role);
        return roleLabel ? `${user.fullName} - ${roleLabel}` : user.fullName;
    };
    const assignedDepartment = []
        .concat(ticket.assignedTo?.role || [])
        .map(role => getRoleLabel(role) || role)
        .join(", ");

    const handlePriorityChange = async priority => {
        if (await stores.ticketStore.changeTicketPriority(ticket._id, {priority})) {
            onChanged();
        }
    };

    const handleVisibilityChange = async isShownToOwner => {
        setVisibilitySubmitting(true);
        const result = await stores.ticketStore.changeTicketVisibility(ticket._id, {
            isShownToOwner
        });
        setVisibilitySubmitting(false);
        if (result) {
            onChanged();
        }
    };

    const handleRolesChange = value => {
        setRoles(value);
        if (value.length) {
            setSelectedUser(undefined);
        }
    };

    const handleUserChange = userId => {
        setSelectedUser(users.find(user => user._id === userId));
        if (userId) {
            setRoles([]);
        }
    };

    const handleAssign = async () => {
        setSubmitting(true);
        let result;
        if (!isOtherMode) {
            result = await stores.ticketStore.claimTicket(ticket._id);
        } else {
            const payload = selectedUser
                ? {type: "user", role: selectedUser.role, userId: selectedUser._id}
                : {type: "role", role: roles};
            result = await stores.ticketStore.assignTicket(ticket._id, payload);
        }
        setSubmitting(false);
        if (result) {
            setIsEditing(false);
            onChanged();
        }
    };

    const handleEditToggle = () => {
        if (isEditing) {
            setRoles(assignedRoles);
            setSelectedUser(undefined);
            setUserSearch("");
        }
        setIsEditing(previous => !previous);
    };

    const isRolesChanged = [...roles].sort().join(",") !== assignedRolesKey;
    const isAssignValid = !isOtherMode || (roles.length > 0 && isRolesChanged) || !!selectedUser;
    const userOptions =
        selectedUser && !users.some(user => user._id === selectedUser._id)
            ? [selectedUser, ...users]
            : users;

    const ownerCategory = ticket.ticketType?.category;
    const showVisibility =
        can("canChangeVisibility") && ["courier", "company"].includes(ownerCategory);

    if (!can("canChangePriority") && !can("canAssign") && !canClaim && !showVisibility) {
        return null;
    }

    const assignButton = (
        <Button
            type="primary"
            loading={submitting}
            disabled={!isAssignValid}
            onClick={handleAssign}
        >
            {t("detail.management.assign", {ns: "tickets"})}
        </Button>
    );

    return (
        <Row gutter={[24, 16]} style={{marginBottom: 16}}>
            {(can("canAssign") || canClaim) && (
                <Col xs={24}>
                    <AssignCard
                        size="small"
                        title={
                            <SectionTitle>
                                <UserOutlined />
                                {t("detail.management.assignTitle", {ns: "tickets"})}
                            </SectionTitle>
                        }
                    >
                        <AssignedBand $background="#e6f4ff" $isEditing={isEditing}>
                            <InfoColumn>
                                {ticket.assignedUser ? (
                                    <InfoItem
                                        title={t("detail.management.assignedUser", {ns: "tickets"})}
                                        icon={<UserOutlined />}
                                    >
                                        {getUserLabel(ticket.assignedUser)}
                                    </InfoItem>
                                ) : (
                                    <InfoItem
                                        title={t("detail.management.assignedDepartment", {
                                            ns: "tickets"
                                        })}
                                        icon={<ApartmentOutlined />}
                                    >
                                        {assignedDepartment}
                                    </InfoItem>
                                )}
                            </InfoColumn>
                            <Tooltip
                                title={t(
                                    isEditing
                                        ? "detail.management.cancelEdit"
                                        : "detail.management.editAssignment",
                                    {ns: "tickets"}
                                )}
                            >
                                <EditToggle
                                    type="text"
                                    icon={isEditing ? <CloseOutlined /> : <EditOutlined />}
                                    onClick={handleEditToggle}
                                />
                            </Tooltip>
                        </AssignedBand>
                        {isEditing && (
                            <>
                                {canClaim && (
                                    <ModeSwitch
                                        value={mode}
                                        onChange={event => setMode(event.target.value)}
                                    >
                                        <Radio.Button value={ASSIGN_MODES.self}>
                                            <UserOutlined />{" "}
                                            {t("detail.management.selfAssign", {ns: "tickets"})}
                                        </Radio.Button>
                                        <Radio.Button value={ASSIGN_MODES.other}>
                                            <TeamOutlined />{" "}
                                            {t("detail.management.otherAssign", {ns: "tickets"})}
                                        </Radio.Button>
                                    </ModeSwitch>
                                )}
                                {isOtherMode ? (
                                    <>
                                        <TargetRow>
                                            <TargetField>
                                                <FieldLabel>
                                                    <ApartmentOutlined />
                                                    {t("detail.management.departmentLabel", {
                                                        ns: "tickets"
                                                    })}
                                                </FieldLabel>
                                                <Select
                                                    mode="multiple"
                                                    showSearch
                                                    optionFilterProp="children"
                                                    style={{width: "100%"}}
                                                    value={roles}
                                                    onChange={handleRolesChange}
                                                    placeholder={t(
                                                        "detail.management.selectRoles",
                                                        {
                                                            ns: "tickets"
                                                        }
                                                    )}
                                                >
                                                    {departmentOptions.map(role => (
                                                        <Select.Option
                                                            key={role.key}
                                                            value={role.key}
                                                        >
                                                            {role.name}
                                                        </Select.Option>
                                                    ))}
                                                </Select>
                                            </TargetField>
                                            <OrBadge>
                                                {t("detail.management.or", {ns: "tickets"})}
                                            </OrBadge>
                                            <TargetField>
                                                <FieldLabel>
                                                    <UserOutlined />
                                                    {t("detail.management.userLabel", {
                                                        ns: "tickets"
                                                    })}
                                                </FieldLabel>
                                                <Select
                                                    showSearch
                                                    allowClear
                                                    filterOption={false}
                                                    style={{width: "100%"}}
                                                    value={selectedUser?._id}
                                                    onChange={handleUserChange}
                                                    onSearch={setUserSearch}
                                                    onBlur={() => setUserSearch("")}
                                                    notFoundContent={t(
                                                        "detail.management.noEligibleUsers",
                                                        {
                                                            ns: "tickets"
                                                        }
                                                    )}
                                                    placeholder={t("detail.management.searchUser", {
                                                        ns: "tickets"
                                                    })}
                                                >
                                                    {userOptions.map(user => (
                                                        <Select.Option
                                                            key={user._id}
                                                            value={user._id}
                                                        >
                                                            {getUserLabel(user)}
                                                        </Select.Option>
                                                    ))}
                                                </Select>
                                            </TargetField>
                                        </TargetRow>
                                        <Alert
                                            type="info"
                                            showIcon
                                            icon={<InfoCircleOutlined />}
                                            message={t("detail.management.assignHint", {
                                                ns: "tickets"
                                            })}
                                            style={{marginTop: 16}}
                                        />
                                        <ActionRow>{assignButton}</ActionRow>
                                    </>
                                ) : (
                                    <SelfAssignRow>
                                        <Input
                                            disabled
                                            prefix={<UserOutlined />}
                                            value={stores.meStore.fullName}
                                        />
                                        {assignButton}
                                    </SelfAssignRow>
                                )}
                            </>
                        )}
                    </AssignCard>
                </Col>
            )}
            {can("canChangePriority") && (
                <Col xs={24} md={8}>
                    <SectionCard
                        size="small"
                        title={
                            <SectionTitle>
                                <FlagOutlined />
                                {t("detail.management.changePriority", {ns: "tickets"})}
                            </SectionTitle>
                        }
                    >
                        <Select
                            value={ticket.priority}
                            onChange={handlePriorityChange}
                            style={{width: "100%"}}
                        >
                            {TICKET_PRIORITIES.map(priority => (
                                <Select.Option key={priority} value={priority}>
                                    {t(`priorities.${priority}`, {ns: "tickets"})}
                                </Select.Option>
                            ))}
                        </Select>
                    </SectionCard>
                </Col>
            )}
            {showVisibility && (
                <Col xs={24} md={16}>
                    <SectionCard
                        size="small"
                        title={
                            <SectionTitle>
                                <EyeOutlined />
                                {t(`ownerVisibility.${ownerCategory}`, {ns: "tickets"})}
                            </SectionTitle>
                        }
                    >
                        <Switch
                            checked={Boolean(ticket.isShownToOwner)}
                            loading={visibilitySubmitting}
                            disabled={IS_TICKET_EXTERNAL_ACCESS_BLOCKED && !ticket.isShownToOwner}
                            onChange={handleVisibilityChange}
                        />
                    </SectionCard>
                </Col>
            )}
        </Row>
    );
};
